import { PenLine, Clock, User as UserIcon } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState } from 'react'

import { FormModal } from '@components/branded-modal'
import { StatusInfo } from '@components/status-info'
import { ClientForm } from '@components/client'
import { AdasBox, AdasButton, AdasCard, AdasDivider, AdasTypography } from '@components/wrapper-components'
import { ccidFormatter, phoneNumberFormat } from '../../../utils/utils'
import { CLIENT_TYPE_ENUM, LABEL_DISPLAY } from '../../../constants'

// Read-only client info modal — mirrors UserInfo's flow on Global Directory. Click the
// edit pencil → see the client at a glance. Tap "Edit" in the footer to swap into the
// existing ClientForm. ClientForm renders bare form fields, so the editing branch wraps
// it in a FormModal (the chrome every other ClientForm caller provides). Used by Global
// Clients.
export const ClientInfoModal = (props) => {
  const { open = true, clientInfo, closeDrawer, onSaveSuccess } = props
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <FormModal open={open} onClose={closeDrawer} title='Edit Client' icon={PenLine} width='max-w-[520px]'>
        <ClientForm
          clientData={clientInfo}
          close={() => setEditing(false)}
          onSaveSuccess={(updated) => {
            setEditing(false)
            onSaveSuccess?.(updated)
            closeDrawer?.()
          }}
          existingClients={[]}
        />
      </FormModal>
    )
  }

  const lastModified = clientInfo?.updated_at || clientInfo?.created_at
  const lastModifiedDate = lastModified ? new Date(lastModified).toLocaleDateString() : ''
  const lastModifiedTime = lastModified
    ? new Date(lastModified).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    : ''

  const rebateLabel =
    clientInfo?.rebate_percent !== 0 && clientInfo?.rebate_percent !== null && clientInfo?.rebate_percent !== undefined
      ? `${clientInfo.rebate_percent}%`
      : '-'
  const centerLine =
    clientInfo?.calibration_center_name && clientInfo?.calibration_center_id != null
      ? `${clientInfo.calibration_center_name} · ${ccidFormatter(clientInfo.calibration_center_id)}`
      : clientInfo?.calibration_center_name || ''

  const footer = (
    <AdasBox className='flex items-center justify-end'>
      <AdasButton startIcon={<PenLine />} aria-label='Edit' color='primary' onClick={() => setEditing(true)}>
        Edit
      </AdasButton>
    </AdasBox>
  )

  return (
    <FormModal open={open} onClose={closeDrawer} title='Client Info' icon={UserIcon} footer={footer}>
      <AdasCard className='adas-card'>
        <span>
          <AdasTypography className='font-semibold inline' variant='h6'>
            {clientInfo?.name || '—'}
          </AdasTypography>
          <StatusInfo
            color={clientInfo?.is_active ? 'primary' : 'default'}
            label={clientInfo?.is_active ? 'Active' : 'Inactive'}
            className='float-right'
          />
        </span>
        <AdasDivider className='my-2 mx-0' />

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          CCC ID
        </AdasTypography>
        <AdasTypography className='font-mono'>{clientInfo?.ccc_client_id || '-'}</AdasTypography>

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          CCC Scrub Requests
        </AdasTypography>
        <AdasTypography>{clientInfo?.calibration_active !== false ? 'Enabled' : 'Disabled'}</AdasTypography>

        {centerLine && (
          <>
            <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
              Calibration Center
            </AdasTypography>
            <AdasTypography>{centerLine}</AdasTypography>
          </>
        )}

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Type
        </AdasTypography>
        <AdasTypography>{CLIENT_TYPE_ENUM[clientInfo?.type] || clientInfo?.type || '-'}</AdasTypography>

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Email
        </AdasTypography>
        <AdasTypography>{clientInfo?.email ? <a href={`mailto:${clientInfo.email}`}>{clientInfo.email}</a> : '-'}</AdasTypography>

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Phone
        </AdasTypography>
        <AdasTypography>
          {clientInfo?.phone ? <a href={`tel:${clientInfo.phone}`}>{phoneNumberFormat(clientInfo.phone)}</a> : '-'}
        </AdasTypography>

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          {LABEL_DISPLAY.MONTHLY_VEHICLECOUNT_LABEL}
        </AdasTypography>
        <AdasTypography>{clientInfo?.vehicle_count ?? '-'}</AdasTypography>

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          {LABEL_DISPLAY.REBATE_DISCOUNT_LABEL}
        </AdasTypography>
        <AdasTypography>{rebateLabel}</AdasTypography>

        <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
          Auto-Apply Rebate
        </AdasTypography>
        <AdasTypography>{clientInfo?.auto_apply_rebate ? 'Enabled' : 'Disabled'}</AdasTypography>

        {(clientInfo?.address1 || clientInfo?.city) && (
          <>
            <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
              Address
            </AdasTypography>
            <AdasTypography>
              {[clientInfo?.address1, clientInfo?.city, clientInfo?.state, clientInfo?.zip].filter(Boolean).join(', ') || '-'}
            </AdasTypography>
          </>
        )}

        {clientInfo?.notes && (
          <>
            <AdasTypography className='my-2 font-semibold' variant='subtitle2'>
              Notes
            </AdasTypography>
            <AdasTypography>{clientInfo.notes}</AdasTypography>
          </>
        )}

        <AdasDivider className='my-2 mx-0' />
        <AdasBox>
          <AdasTypography variant='overline' className='font-semibold text-[#6b7280]'>
            RECORD INFORMATION
          </AdasTypography>
          <AdasCard className='flex p-2 rounded-md border-l-4 border-[#b7bec8] bg-[#fff]'>
            <Clock style={{ width: '20px', height: '20px', color: '#6b7280', marginRight: '8px' }} />
            <AdasTypography variant='body2' className='italic text-[#4b5563]'>
              <strong>Last Modified:</strong> {lastModifiedDate || '—'}
              {lastModifiedTime ? ` @ ${lastModifiedTime}` : ''}
            </AdasTypography>
          </AdasCard>
        </AdasBox>
      </AdasCard>
    </FormModal>
  )
}

ClientInfoModal.propTypes = {
  open: PropTypes.bool,
  clientInfo: PropTypes.object.isRequired,
  closeDrawer: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func
}
